/* ─────────────────────────────────────────────────────────────────
   LECTOR DE CÉDULAS COLOMBIANAS (pistola / scanner de código de barras)

   Las pistolas lectoras se comportan como un teclado: "escriben" muy
   rápido el contenido del código y terminan con Enter o Tab. Pero el
   contenido que envían depende de cómo se configure el equipo y del
   documento leído, por eso aquí se soportan varios formatos:

     1. PDF417 de la cédula amarilla (marca `PubDSK_`) con el número y
        los nombres en posiciones fijas (Registraduría).
     2. Columnas separadas por TAB, `;`, `|`, `,` o `_`, que es como
        quedan configurados la mayoría de lectores, por ejemplo:
        `ID,APELLIDO1,APELLIDO2,NOMBRE1,NOMBRE2,GENERO,07101992`
        `1234567890;PEREZ;GOMEZ;CAMILO;ANDRES;M;1985-04-17;O+`
     3. Una sola línea separada por espacios:
        `1234567890 MARTINEZ GOMEZ JUAN CARLOS F 1980-01-15 O+`
     4. Zona de lectura mecánica (MRZ) de la cédula digital o del
        pasaporte (formatos TD1, TD2 y TD3).
     5. Sólo el número (`1234567890`) o el número con prefijo de tipo
        de documento (`CC 1234567890`, `CEDULA: 1234567890`).
     6. QR de la cédula digital: está cifrado, no se puede leer. Se
        detecta y se avisa para usar el MRZ o el teclado.

   Uso típico:
     const lectura = parsearLecturaCedula(cadenaCruda);
     if (lectura.ok) { usar lectura.numeroDocumento, lectura.nombre1, ... }

   Nota sobre nombres: los lectores casi siempre entregan primero los
   APELLIDOS y luego los NOMBRES (configuración estándar de fábrica).
   Si su equipo los entrega al revés, cambie ORDEN_NOMBRES.
   ───────────────────────────────────────────────────────────────── */

/* Orden en que los formatos de columnas / espacios traen los datos.
   'apellidos_primero' → APELLIDO1, APELLIDO2, NOMBRE1, NOMBRE2
   'nombres_primero'   → NOMBRE1, NOMBRE2, APELLIDO1, APELLIDO2 */
export const ORDEN_NOMBRES = 'apellidos_primero';

/* Etiqueta → código de tipo de documento (debe coincidir con
   `tiposDocumentoLabels` de la pantalla del kiosko) */
const TIPOS_POR_ETIQUETA = {
    CC: 'CC', CEDULA: 'CC', CEDULADECIUDADANIA: 'CC', CIUDADANIA: 'CC', CEDULACIUDADANIA: 'CC',
    TI: 'TI', TARJETAIDENTIDAD: 'TI',
    RC: 'RC', REGISTROCIVIL: 'RC',
    CE: 'CE', CEDULADEEXTRANJERIA: 'CE', EXTRANJERIA: 'CE',
    PA: 'PA', PASAPORTE: 'PA',
    PE: 'PE', PERMISOESPECIALDEPERMANENCIA: 'PE',
    PT: 'PT', PERMISOPORPROTECCIONTEMPORAL: 'PT',
};

/* Etiquetas de encabezado que algunos lectores agregan como primera
   fila (se descartan para no confundirlas con nombres) */
const ETIQUETAS_ENCABEZADO = new RegExp(
    '^(NUMERO|NUM|NRO|NUMERODEDOCUMENTO|NUMERODECEDULA|CEDULA|CEDULADECIUDADANIA|DOCUMENTO|' +
    'IDENTIFICACION|ID|PRIMERAPELLIDO|SEGUNDOAPELLIDO|APELLIDO1|APELLIDO2|APELLIDOS|' +
    'PRIMERNOMBRE|SEGUNDONOMBRE|NOMBRE1|NOMBRE2|NOMBRES|SEXO|GENERO|RH|TIPOSANGRE|' +
    'TIPODESANGRE|FECHA|FECHANACIMIENTO|FECHADENACIMIENTO|TIPODOCUMENTO)$'
);

/* Formatos para mostrar en pantalla */
const FORMATOS_LEGIBLES = {
    pubdsk: 'PDF417 (cédula amarilla)',
    delimitado: 'columnas',
    espacios: 'texto separado por espacios',
    prefijo: 'número con prefijo',
    plano: 'sólo número',
    'mrz-td1': 'MRZ (cédula digital)',
    'mrz-td2': 'MRZ (documento)',
    'mrz-td3': 'MRZ (pasaporte)',
    'qr-cifrado': 'QR cifrado',
};

export const describirFormato = (formato) =>
    FORMATOS_LEGIBLES[formato] || formato || 'desconocido';

/* ─── Utilidades de limpieza y validación ─────────────────────── */

// Relleno del PDF417: byte NUL. Se maneja como texto (no como regex)
// porque el linter de CRA no permite caracteres de control en regex.
const RELLENO = '\u0000';

// Quita el relleno del PDF417 (NUL, carácter de reemplazo, BOM)
export const limpiarTexto = (valor) => String(valor ?? '')
    .split(RELLENO).join(' ')
    .split('\uFFFD').join(' ')
    .replace(/[\uFEFF\u200B]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const sinEspacios = (valor) => String(valor ?? '').replace(/[.\s:/-]/g, '');
const soloDigitos = (valor) => /^\d+$/.test(String(valor ?? '').trim());

// Número de documento válido: entre 5 y 12 dígitos y no todo ceros
export const esNumeroDocumento = (valor) => {
    const s = String(valor ?? '').trim();
    return /^\d{5,12}$/.test(s) && !/^0+$/.test(s);
};

// El PDF417 rellena el número con ceros a la izquierda
const quitarCerosIzquierda = (valor) => String(valor ?? '').replace(/^0+/, '') || '';

const soloLetras = (valor) => String(valor ?? '')
    .replace(/[^A-Za-zÁÉÍÓÚÜÑáéíóúüñ ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

const pad2 = (n) => String(n).padStart(2, '0');

const fechaDesdeComponentes = (anio, mes, dia) => {
    const y = Number(anio);
    const m = Number(mes);
    const d = Number(dia);
    const anioActual = new Date().getFullYear();
    if (!y || y < 1900 || y > anioActual) return null;
    if (!m || m < 1 || m > 12) return null;
    if (!d || d < 1 || d > 31) return null;
    return `${y}-${pad2(m)}-${pad2(d)}`;
};

/* Normaliza fechas de cualquiera de los formatos que usan los
   lectores: 1980-01-15, 15/01/1980, 19800115, 15011980 */
export const normalizarFecha = (valor) => {
    const s = String(valor ?? '').replace(/[^\d/-]/g, '');
    if (s.length < 8) return null;

    let m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
    if (m) return fechaDesdeComponentes(m[1], m[2], m[3]);

    m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (m) return fechaDesdeComponentes(m[3], m[2], m[1]);

    m = s.match(/^(\d{4})(\d{2})(\d{2})$/);
    if (m) {
        const iso = fechaDesdeComponentes(m[1], m[2], m[3]);
        if (iso) return iso;
    }

    m = s.match(/^(\d{2})(\d{2})(\d{4})$/);
    if (m) return fechaDesdeComponentes(m[3], m[2], m[1]);

    return null;
};

const normalizarSexo = (valor) => {
    const s = sinEspacios(valor).toUpperCase();
    if (['M', 'MASCULINO', 'H', 'HOMBRE'].includes(s)) return 'M';
    if (['F', 'FEMENINO', 'MUJER'].includes(s)) return 'F';
    return null;
};

const normalizarRh = (valor) => {
    const s = String(valor ?? '').toUpperCase().replace(/\s+/g, '');
    return /^(A|B|AB|O)[+-]$/.test(s) ? s : null;
};

const tipoDesdeEtiqueta = (valor) => {
    const s = sinEspacios(valor).toUpperCase();
    return TIPOS_POR_ETIQUETA[s] || null;
};

const esEtiquetaEncabezado = (valor) =>
    ETIQUETAS_ENCABEZADO.test(sinEspacios(valor).toUpperCase());

// ¿El campo "parece" una fecha aunque no sea válida (p. ej. 19XX-01-15)?
const pareceFecha = (valor) =>
    /^\d{1,4}[X\d]{0,4}[-/][\dX]{1,2}[-/][\dX]{1,4}$/i.test(String(valor ?? '').trim());

/* Asigna los campos alfabéticos a nombres / apellidos según el orden
   configurado en ORDEN_NOMBRES */
const asignarNombres = (campos, orden = ORDEN_NOMBRES) => {
    const letras = campos.map(soloLetras).filter(Boolean).slice(0, 4);
    const r = { nombre1: '', nombre2: '', apellido1: '', apellido2: '' };

    if (letras.length === 1) {
        r.nombre1 = letras[0];
        return r;
    }

    if (orden === 'nombres_primero') {
        [r.nombre1, r.nombre2, r.apellido1, r.apellido2] = letras;
    } else {
        [r.apellido1, r.apellido2, r.nombre1, r.nombre2] = letras;
    }
    return r;
};

/* ─── Estrategia 1: PDF417 de la cédula amarilla (PubDSK) ─────── */

const RE_PUBDSK = /PubDSK_[0-9]?/i;

/* Posiciones fijas del marco de 531 bytes (o 200 truncados).
   El marcador PubDSK_1 está en el byte 24 (o 13 si la lectura viene
   truncada). Tabla de campos tomada del formato de la Registraduría. */
const pubdskPorPosiciones = (crudo, idxMarca) => {
    const previos = crudo.slice(Math.max(0, idxMarca - 10), idxMarca);
    const nulos = previos.split(RELLENO).length - 1;
    const inicio = idxMarca - (nulos > 4 ? 24 : 13);
    if (inicio < 0) return null;

    const recorte = (a, b) => limpiarTexto(crudo.slice(inicio + a, inicio + b));

    const numero = (recorte(48, 58).match(/\d{5,12}/) || [])[0];
    if (!esNumeroDocumento(numero)) return null;

    const extra = recorte(150, 172);
    const anio = recorte(152, 156);
    const mes = recorte(156, 158);
    const dia = recorte(158, 160);

    let fechaNacimiento = fechaDesdeComponentes(anio, mes, dia);
    if (!fechaNacimiento) {
        const m = extra.replace(/\s/g, '').match(/(\d{4})(\d{2})(\d{2})/);
        if (m) fechaNacimiento = fechaDesdeComponentes(m[1], m[2], m[3]);
    }

    const sexo = normalizarSexo(recorte(151, 152)) || normalizarSexo((extra.match(/\b[MF]\b/) || [])[0]);
    const rh = normalizarRh(recorte(166, 168)) || normalizarRh((extra.match(/[ABO]{1,2}[+-]/) || [])[0]);

    return {
        numeroDocumento: quitarCerosIzquierda(numero),
        apellido1: soloLetras(recorte(58, 81)),
        apellido2: soloLetras(recorte(81, 104)),
        nombre1: soloLetras(recorte(104, 127)),
        nombre2: soloLetras(recorte(127, 150)),
        fechaNacimiento,
        sexo,
        rh,
    };
};

/* Cuando la lectura no conserva los NUL (caso típico de una pistola en
   modo HID o de Windows) las posiciones fijas ya no sirven. Se
   reconstruye el número aprovechando que va relleno a 10 dígitos justo
   antes del primer nombre, y los nombres se toman sólo si vinieron
   separados (si quedaron pegados no se adivinan). */
const pubdskPorHeuristica = (crudo) => {
    const desdeMarca = crudo.slice(crudo.search(RE_PUBDSK));
    // Se quita la marca para que "PubDSK_1" no se confunda con un nombre
    const texto = limpiarTexto(desdeMarca.replace(RE_PUBDSK, ' '));
    const compacto = texto.replace(/\s+/g, '');

    // Número: los 10 dígitos inmediatamente anteriores al primer nombre
    let numero = null;
    const idxLetra = compacto.search(/[A-Za-zÁÉÍÓÚÑ]/);
    if (idxLetra > 0) {
        const previos = compacto.slice(0, idxLetra).replace(/\D/g, '');
        if (previos.length >= 10) numero = previos.slice(-10);
    }
    if (!esNumeroDocumento(numero)) {
        numero = (compacto.match(/\d{10}/) || [])[0];
    }
    if (!esNumeroDocumento(numero)) return null;

    // Nombres: palabras alfabéticas sueltas. Si un nombre quedó pegado al
    // número ("0051907053GONZALEZ") se rescata su parte alfabética.
    const alfabeticos = [];
    texto.split(/\s+/).filter(Boolean).forEach((palabra) => {
        if (palabra.length > 1 && /^[A-Za-zÁÉÍÓÚÑ]+$/.test(palabra)) {
            alfabeticos.push(palabra);
            return;
        }
        const pegado = palabra.match(/^\d+([A-Za-zÁÉÍÓÚÑ]{2,})$/);
        if (pegado) alfabeticos.push(pegado[1]);
    });

    // En el PDF417 el sexo va pegado a la fecha de nacimiento (M19670128)
    const juntos = compacto.match(/([MF])(\d{8})/);
    let fechaNacimiento = juntos ? normalizarFecha(juntos[2]) : null;
    let sexo = juntos ? normalizarSexo(juntos[1]) : null;

    if (!fechaNacimiento) {
        const fechas = compacto.match(/\d{8}/g) || [];
        fechaNacimiento = fechas.map((f) => normalizarFecha(f)).find(Boolean) || null;
    }
    if (!sexo) {
        sexo = normalizarSexo((texto.match(/(?:^|\s)([MF])(?=\s|$)/) || [])[1]);
    }

    return {
        ...asignarNombres(alfabeticos),
        numeroDocumento: quitarCerosIzquierda(numero),
        fechaNacimiento,
        sexo,
        rh: normalizarRh((compacto.match(/(?:AB|A|B|O)[+-]/i) || [])[0]),
    };
};

const probarPubDSK = (crudo) => {
    const idx = crudo.search(RE_PUBDSK);
    if (idx < 0) return null;

    // Con los NUL de relleno presentes se pueden usar las posiciones fijas
    if (crudo.includes('\u0000')) {
        const porPosiciones = pubdskPorPosiciones(crudo, idx);
        if (porPosiciones) return porPosiciones;
    }
    return pubdskPorHeuristica(crudo);
};

/* ─── Clasificación de campos (formatos separados por columnas) ── */

const clasificarCampos = (partes, ordenSugerido) => {
    const campos = partes.filter((p) => !esEtiquetaEncabezado(p));
    if (!campos.length) return null;

    let numero = null;
    let tipoDocumento = null;
    let idxNumero = -1;

    campos.forEach((campo, i) => {
        if (numero || esEtiquetaEncabezado(campo)) return;

        const limpio = String(campo).trim();
        const letrasNumeros = limpio.match(/^([A-Za-z.]{2,6})\s*[-:]?\s*(\d{5,12})$/);
        if (letrasNumeros) {
            const tipo = tipoDesdeEtiqueta(letrasNumeros[1]);
            if (tipo) {
                tipoDocumento = tipo;
                numero = letrasNumeros[2];
                idxNumero = i;
                return;
            }
        }

        if (!/[\d]/.test(limpio)) return;
        if (pareceFecha(limpio)) return;

        const digitos = limpio.replace(/\D/g, '');
        if (esNumeroDocumento(digitos) && !normalizarFecha(digitos)) {
            numero = digitos;
            idxNumero = i;
            if (soloDigitos(limpio) === false) {
                tipoDocumento = tipoDocumento || tipoDesdeEtiqueta(limpio);
            }
        }
    });

    if (!esNumeroDocumento(numero)) return null;

    // El tipo de documento puede venir en su propia columna ("CC", "TI", ...)
    if (!tipoDocumento) {
        for (const campo of campos) {
            const tipo = tipoDesdeEtiqueta(campo);
            if (tipo) { tipoDocumento = tipo; break; }
        }
    }

    const letras = [];
    let fechaNacimiento = null;
    let sexo = null;
    let rh = null;

    campos.forEach((campo, i) => {
        if (i === idxNumero) return;
        const limpio = String(campo).trim();
        if (!limpio || esEtiquetaEncabezado(limpio)) return;
        // Los códigos de tipo de documento no son nombres
        if (tipoDesdeEtiqueta(limpio)) return;

        const f = normalizarFecha(limpio);
        if (f && !fechaNacimiento) { fechaNacimiento = f; return; }
        if (pareceFecha(limpio)) return;

        if (!sexo) {
            const s = normalizarSexo(limpio);
            if (s) { sexo = s; return; }
        }
        if (!rh) {
            const r = normalizarRh(limpio);
            if (r) { rh = r; return; }
        }
        if (/[A-Za-zÁÉÍÓÚÑ]/.test(limpio)) letras.push(limpio);
    });

    return {
        ...asignarNombres(letras, ordenSugerido),
        numeroDocumento: numero,
        tipoDocumento: tipoDocumento || undefined,
        fechaNacimiento,
        sexo,
        rh,
    };
};

const DELIMITADORES = ['\t', ';', '|', ',', '_'];

const probarDelimitado = (crudo) => {
    // El marcador PubDSK_1 trae un "_" que no es separador de campos
    if (RE_PUBDSK.test(crudo)) return null;

    // Ojo: no se colapsan los espacios aquí porque el tabulador es uno de
    // los delimitadores más usados por los lectores
    const texto = String(crudo ?? '')
        .split(RELLENO).join(' ')
        .split('\uFFFD').join(' ')
        .trim();
    if (!texto) return null;

    for (const delimitador of DELIMITADORES) {
        if (!texto.includes(delimitador)) continue;

        const partes = texto
            .split(delimitador)
            .map((p) => limpiarTexto(p))
            .filter((p) => p !== '');
        if (partes.length < 2) continue;

        const resultado = clasificarCampos(partes);
        if (resultado && resultado.numeroDocumento) return resultado;
    }
    return null;
};

/* ─── Estrategia: una sola línea separada por espacios ────────── */

const probarPorEspacios = (crudo) => {
    const texto = limpiarTexto(crudo);
    if (!/[A-Za-zÁÉÍÓÚÑ]/.test(texto)) return null;
    if (!/\d/.test(texto)) return null;
    // El PDF417 se interpreta con su propia estrategia
    if (RE_PUBDSK.test(texto)) return null;

    // El formato "numero  nombre1 nombre2 apellido1 apellido2" usa doble
    // espacio como separador: en ese caso los nombres van primero
    const ordenSugerido = /\d {2,}/.test(texto) ? 'nombres_primero' : undefined;

    const partes = texto.split(/\s+/).filter(Boolean);
    if (partes.length < 2) return null;

    return clasificarCampos(partes, ordenSugerido);
};

/* ─── Estrategia: número con prefijo de tipo de documento ─────── */

const probarPrefijo = (crudo) => {
    const texto = limpiarTexto(crudo);
    const m = texto.match(/^([A-Za-z.]{2,20})\s*[-:]?\s*(\d{5,12})$/);
    if (!m) return null;

    const tipoDocumento = tipoDesdeEtiqueta(m[1]);
    if (!tipoDocumento) return null;

    return { numeroDocumento: m[2], tipoDocumento };
};

/* ─── Estrategia: sólo el número de documento ─────────────────── */

const probarPlano = (crudo) => {
    const texto = limpiarTexto(crudo).replace(/[\s-]/g, '');
    if (!esNumeroDocumento(texto)) return null;
    return { numeroDocumento: texto };
};

/* ─── Estrategia: MRZ (cédula digital / pasaporte) ────────────── */

const fechaMRZ = (valor) => {
    const m = /^(\d{2})(\d{2})(\d{2})$/.exec(String(valor ?? ''));
    if (!m) return null;
    const yy = Number(m[1]);
    const anio = yy > (new Date().getFullYear() % 100) ? 1900 + yy : 2000 + yy;
    return fechaDesdeComponentes(anio, m[2], m[3]);
};

const probarMRZ = (crudo) => {
    let lineas = String(crudo ?? '')
        .split(RELLENO).join('\n')
        .split(/\r\n|\r|\n/)
        .map((l) => l.trim())
        .filter(Boolean);

    // Sin saltos de línea: TD1 = 90 caracteres en 3 filas de 30
    if (lineas.length === 1 && /^[A-Z0-9<]{88,92}$/.test(lineas[0])) {
        lineas = [lineas[0].slice(0, 30), lineas[0].slice(30, 60), lineas[0].slice(60, 90)];
    }

    if (lineas.length !== 2 && lineas.length !== 3) return null;
    if (!lineas.every((l) => /^[A-Z0-9<]+$/.test(l))) return null;

    const largo = lineas[0].length;
    if (largo < 28 || largo > 46) return null;

    const l1 = lineas[0];
    const l2 = lineas[1];

    // TD1 (cédula digital): 3 filas de 30
    if (lineas.length === 3) {
        const numero = l1.slice(5, 14).replace(/</g, '').replace(/^0+/, '');
        if (!esNumeroDocumento(numero)) return null;
        return {
            numeroDocumento: numero,
            fechaNacimiento: fechaMRZ(l2.slice(0, 6)),
            sexo: normalizarSexo(l2.slice(7, 8)),
            formatoPersonalizado: 'mrz-td1',
        };
    }

    // TD3 (pasaporte): 2 filas de 44
    if (largo >= 40) {
        const numero = l2.slice(0, 9).replace(/</g, '').replace(/^0+/, '');
        if (!esNumeroDocumento(numero)) return null;
        const esPasaporte = /^P/.test(l1);
        return {
            numeroDocumento: numero,
            tipoDocumento: esPasaporte ? 'PA' : undefined,
            fechaNacimiento: fechaMRZ(l2.slice(13, 19)),
            sexo: normalizarSexo(l2.slice(20, 21)),
            formatoPersonalizado: esPasaporte ? 'mrz-td3' : 'mrz-td2',
        };
    }

    // TD2 (documento de identidad): 2 filas de 36
    const numero = l1.slice(5, 14).replace(/</g, '').replace(/^0+/, '');
    if (!esNumeroDocumento(numero)) return null;
    return {
        numeroDocumento: numero,
        fechaNacimiento: fechaMRZ(l2.slice(0, 6)),
        sexo: normalizarSexo(l2.slice(7, 8)),
        formatoPersonalizado: 'mrz-td2',
    };
};

/* ─── Detección del QR cifrado de la cédula digital ───────────── */

const RE_QR_CIFRADO = /(registraduria\.gov\.co|ceduladigital|rn[eo]c)/i;

/* ─── Estrategias disponibles, en orden de prioridad ──────────── */

const ESTRATEGIAS = [
    { nombre: 'pubdsk', probar: probarPubDSK },
    { nombre: 'mrz', probar: probarMRZ },
    { nombre: 'delimitado', probar: probarDelimitado },
    { nombre: 'prefijo', probar: probarPrefijo },
    { nombre: 'espacios', probar: probarPorEspacios },
    { nombre: 'plano', probar: probarPlano },
];

/* Punto de entrada: convierte la cadena cruda del lector en datos
   utilizables por el kiosko */
export const parsearLecturaCedula = (crudo) => {
    const texto = String(crudo ?? '');
    const base = { ok: false, formato: 'desconocido', crudo: texto };

    if (!texto.trim()) return { ...base, aviso: 'Lectura vacía' };

    if (RE_QR_CIFRADO.test(texto) && !RE_PUBDSK.test(texto)) {
        return {
            ...base,
            formato: 'qr-cifrado',
            aviso: 'El QR de la cédula digital está cifrado: use el MRZ o digite el número.',
        };
    }

    for (const estrategia of ESTRATEGIAS) {
        let resultado = null;
        try {
            resultado = estrategia.probar(texto);
        } catch (e) {
            resultado = null;
        }
        if (resultado && esNumeroDocumento(resultado.numeroDocumento)) {
            const formato = resultado.formatoPersonalizado || estrategia.nombre;
            const { formatoPersonalizado, ...datos } = resultado;
            return { ...base, ...datos, ok: true, formato };
        }
    }

    return {
        ...base,
        aviso: 'No se reconoce el formato de la lectura. Digite el número con el teclado.',
    };
};

/* ¿La cadena tiene pinta de ser una lectura de pistola y no de alguien
   escribiendo a mano? Se usa para no reaccionar a pulsaciones sueltas. */
export const pareceLecturaScanner = (crudo) => {
    const texto = String(crudo ?? '');
    if (texto.length < 4) return false;
    if (RE_PUBDSK.test(texto)) return true;
    if (/[\t;|]/.test(texto)) return true;
    if (/,\d|\d,/.test(texto)) return true;
    if (/\d{6,12}/.test(texto)) return true;
    if (/^[A-Za-z.]{2,20}\s*[-:]?\s*\d{5,12}$/.test(texto.trim())) return true;
    if (/^[A-Z0-9<]{28,}$/.test(texto.trim())) return true;
    return false;
};

/* Pequeño ayudante para armar el nombre completo (mismo formato que
   usa la pantalla al confirmar los datos) */
export const nombreCompletoDesdeLectura = (lectura = {}) =>
    [lectura.nombre1, lectura.nombre2, lectura.apellido1, lectura.apellido2]
        .filter(Boolean)
        .join(' ')
        .toUpperCase();

export default parsearLecturaCedula;
